import type { HouseholdSharingPort } from '../application/household-sharing';
import type { InventoryService } from '../application/inventory-service';

const marker = 'frozen-save-federated-inventory';
const lastAccount = 'frozen-save-federated-local-account';
export const clearFederatedBootstrap = (): void => {
  window.localStorage.removeItem(marker);
  window.localStorage.removeItem(lastAccount);
};

// Le marqueur choisit le mode de chargement ; seule la session native choisit le compte.
export const federatedBootstrapRequested = (url: URL, storage: Storage): boolean =>
  url.searchParams.get('my-dohm-return') === '1' || storage.getItem(marker) === '1';

export const federatedInventoryDatabase = async (
  base: string, sharing: HouseholdSharingPort, storage: Storage, freshReturn = true,
): Promise<{ databaseName: string; hasHousehold: boolean }> => {
  if (sharing.restoreFederatedSession == null) throw new Error('HOUSEHOLD_NOT_CONFIGURED');
  const session = await sharing.restoreFederatedSession();
  // Hors ligne, réouvrir seulement le cache précédemment choisi ; jamais remplacer un nouveau grant.
  const cachedAccount = storage.getItem(lastAccount);
  if (!freshReturn && session.state === 'unavailable' && /^fsa_[A-Za-z0-9_-]{24,80}$/u.test(cachedAccount ?? '')) {
    return { databaseName: `${base}-account-${cachedAccount!}`, hasHousehold: false };
  }
  if (session.state !== 'connected') throw new Error(session.state === 'disconnected'
    ? 'HOUSEHOLD_SESSION_REQUIRED' : 'HOUSEHOLD_REMOTE_REQUIRED');
  storage.setItem(marker, '1');
  storage.setItem(lastAccount, session.productSubjectId);
  return { databaseName: `${base}-account-${session.productSubjectId}`, hasHousehold: session.households.length > 0 };
};

export const restoreFederatedInventory = async (
  service: InventoryService, sharing: HouseholdSharingPort, hasHousehold: boolean,
): Promise<void> => {
  await service.initialize();
  if (!hasHousehold) return;
  const result = await sharing.synchronize(await service.snapshot(), true);
  if (result.state === 'awaiting_key') throw new Error('HOUSEHOLD_KEY_REQUIRED');
  if (result.state !== 'remote') throw new Error('HOUSEHOLD_REMOTE_REQUIRED');
  await service.restoreBackup(result.snapshot);
  await sharing.confirmRemoteApplied(await service.snapshot(), result.checkpoint);
};
